import { createClient, SupabaseClient, Session } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const DEFAULT_COLYSEUS_URL = import.meta.env.VITE_COLYSEUS_URL || 'ws://localhost:2567';

export interface ServerInfo {
  id: string;
  display_name: string;
  region: string;
  colyseus_url: string;
  status: string;
  max_characters: number;
  online_players?: number;
}

export interface CharacterInfo {
  id: string;
  username: string;
  credits: number;
  reputation: number;
  shipClassId: string | null;
  constellationX: number;
  constellationY: number;
  constellationName: string;
  positionX: number;
  positionY: number;
  createdAt: string;
}

export interface AccountInfo {
  id: string;
  displayName: string | null;
  avatarUrl: string | null;
}

class AuthService {
  private supabase: SupabaseClient;
  private session: Session | null = null;

  // Server selection state
  private _selectedServer: ServerInfo | null = null;

  // Character selection state
  private _selectedCharacterId: string | null = null;
  private _account: AccountInfo | null = null;
  private _characters: CharacterInfo[] = [];
  private _maxCharacters: number = 3;

  // Store player's current constellation info after character selection
  private playerConstellation: { x: number; y: number; name: string } | null = null;

  constructor() {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      throw new Error('Missing Supabase environment variables');
    }

    this.supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

    // Listen for auth changes
    this.supabase.auth.onAuthStateChange((_event, session) => {
      this.session = session;
    });
  }

  // Convert WebSocket URL to HTTP URL for API calls
  private getApiUrl(): string {
    const url = this.getColyseusUrl();
    return url.replace('wss://', 'https://').replace('ws://', 'http://');
  }

  // Get the Colyseus URL for the selected server (or default for dev)
  getColyseusUrl(): string {
    return this._selectedServer?.colyseus_url || DEFAULT_COLYSEUS_URL;
  }

  async initialize(): Promise<Session | null> {
    const { data } = await this.supabase.auth.getSession();
    this.session = data.session;
    return this.session;
  }

  async loginWithDiscord(): Promise<void> {
    const { error } = await this.supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: {
        redirectTo: window.location.origin,
        scopes: 'identify',
      },
    });

    if (error) {
      console.error('Discord login error:', error);
      throw error;
    }
  }

  async loginWithGoogle(): Promise<void> {
    const { error } = await this.supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });

    if (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }

  async loginWithGithub(): Promise<void> {
    const { error } = await this.supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo: window.location.origin,
      },
    });

    if (error) {
      console.error('GitHub login error:', error);
      throw error;
    }
  }

  async loginWithEmail(email: string, password: string): Promise<{ needsVerification: boolean }> {
    // First try to sign in
    const { data, error: signInError } = await this.supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (signInError) {
      // If invalid credentials, could be new user - try sign up
      if (signInError.message.includes('Invalid login credentials')) {
        const { error: signUpError } = await this.supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin,
          },
        });

        if (signUpError) {
          console.error('Email signup error:', signUpError);
          throw signUpError;
        }

        // Sign up succeeded - user needs to verify email
        return { needsVerification: true };
      }

      console.error('Email login error:', signInError);
      throw signInError;
    }

    this.session = data.session;
    return { needsVerification: false };
  }

  async resetPassword(email: string): Promise<void> {
    const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    await this.supabase.auth.signOut();
    this.session = null;
    this._selectedServer = null;
    this._selectedCharacterId = null;
    this._account = null;
    this._characters = [];
    this.playerConstellation = null;
  }

  getAccessToken(): string | null {
    return this.session?.access_token ?? null;
  }

  isAuthenticated(): boolean {
    return this.session !== null;
  }

  getUserInfo(): { id: string; username: string; avatarUrl: string | null } | null {
    if (!this.session?.user) return null;

    const meta = this.session.user.user_metadata;
    // Extract username from metadata (works with any auth provider)
    const username = meta?.full_name || meta?.name || meta?.user_name || meta?.preferred_username || this.session.user.email?.split('@')[0] || 'Pilot';
    const avatarUrl = meta?.avatar_url || meta?.picture || null;

    return {
      id: this.session.user.id,
      username,
      avatarUrl,
    };
  }

  getSession(): Session | null {
    return this.session;
  }

  // ==================== SERVER SELECTION ====================

  // Fetch server list from any available server endpoint
  async getServers(): Promise<ServerInfo[]> {
    try {
      // Use the default server URL to fetch the list
      const apiUrl = DEFAULT_COLYSEUS_URL.replace('wss://', 'https://').replace('ws://', 'http://');
      const response = await fetch(`${apiUrl}/api/servers`);
      if (!response.ok) return [];
      const data = await response.json();
      return data.servers || [];
    } catch (error) {
      console.error('Failed to fetch servers:', error);
      return [];
    }
  }

  selectServer(server: ServerInfo): void {
    this._selectedServer = server;
  }

  get selectedServer(): ServerInfo | null {
    return this._selectedServer;
  }

  // ==================== ACCOUNT & CHARACTERS ====================

  // Ensure account exists on the selected server and fetch character list
  async ensureAccount(): Promise<{
    account: AccountInfo;
    characters: CharacterInfo[];
    canCreateMore: boolean;
    maxCharacters: number;
    suggestedUsername: string;
  } | null> {
    if (!this.session?.access_token) {
      console.warn('Cannot ensure account: no session');
      return null;
    }

    try {
      const apiUrl = this.getApiUrl();
      const response = await fetch(`${apiUrl}/api/ensure-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.session.access_token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('ensure-account failed:', error);
        return null;
      }

      const data = await response.json();
      this._account = data.account;
      this._characters = data.characters || [];
      this._maxCharacters = data.maxCharacters || 3;

      return {
        account: data.account,
        characters: data.characters,
        canCreateMore: data.canCreateMore,
        maxCharacters: data.maxCharacters,
        suggestedUsername: data.suggestedUsername,
      };
    } catch (error) {
      console.error('ensure-account error:', error);
      return null;
    }
  }

  get account(): AccountInfo | null {
    return this._account;
  }

  get characters(): CharacterInfo[] {
    return this._characters;
  }

  get maxCharacters(): number {
    return this._maxCharacters;
  }

  selectCharacter(characterId: string): void {
    this._selectedCharacterId = characterId;
    const char = this._characters.find(c => c.id === characterId);
    if (char) {
      this.playerConstellation = {
        x: char.constellationX,
        y: char.constellationY,
        name: char.constellationName,
      };
    }
  }

  get selectedCharacterId(): string | null {
    return this._selectedCharacterId;
  }

  get selectedCharacter(): CharacterInfo | null {
    if (!this._selectedCharacterId) return null;
    return this._characters.find(c => c.id === this._selectedCharacterId) || null;
  }

  // Get player's current constellation info
  getPlayerConstellation(): { x: number; y: number; name: string } | null {
    return this.playerConstellation;
  }

  // Set constellation info (e.g., after character creation - new player starts in Origin)
  setPlayerConstellation(x: number, y: number, name: string): void {
    this.playerConstellation = { x, y, name };
  }

  // Check if a username is available
  async checkUsername(username: string): Promise<{ available: boolean; reason: string | null }> {
    if (!this.session?.access_token) {
      return { available: false, reason: 'Not authenticated' };
    }

    try {
      const apiUrl = this.getApiUrl();
      const response = await fetch(`${apiUrl}/api/check-username`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.session.access_token}`,
        },
        body: JSON.stringify({ username }),
      });

      if (!response.ok) {
        return { available: false, reason: 'Server error' };
      }

      return await response.json();
    } catch (error) {
      console.error('check-username error:', error);
      return { available: false, reason: 'Network error' };
    }
  }

  // Create character for account
  async createCharacter(
    username: string,
    opts: { skipTutorial?: boolean } = {},
  ): Promise<{
    success: boolean;
    player?: { id: string; username: string; credits: number; constellationX: number; constellationY: number; constellationName: string | null };
    error?: string;
  }> {
    if (!this.session?.access_token) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const apiUrl = this.getApiUrl();
      const response = await fetch(`${apiUrl}/api/create-character`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.session.access_token}`,
        },
        body: JSON.stringify({ username, skipTutorial: opts.skipTutorial === true }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Failed to create character' };
      }

      return { success: true, player: data.player };
    } catch (error) {
      console.error('create-character error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  // Delete character permanently
  async deleteCharacter(characterId: string): Promise<{ success: boolean; error?: string }> {
    if (!this.session?.access_token) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const apiUrl = this.getApiUrl();
      const response = await fetch(`${apiUrl}/api/delete-character`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.session.access_token}`,
        },
        body: JSON.stringify({ characterId }),
      });

      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error || 'Failed to delete character' };
      }

      return { success: true };
    } catch (error) {
      console.error('delete-character error:', error);
      return { success: false, error: 'Network error' };
    }
  }
}

// Singleton instance
export const authService = new AuthService();
